import { CommonContentWrapper } from "../../common/components/content_wrapper/CommonContentWrapper";
import { Divider, Padding, Row, SizedBox } from "../../common/components/CommonBaseComponents";
import { PageTitle } from "../../common/components/PageTitle";
import CustomPeriodButton from "../../common/components/button/CustomPeriodButton";
import { useEffect, useState } from "react";
import styled from "styled-components";
import CustomGraphCard from "../../common/components/card/CustomGraphCard";
import SecondaryButton from "../../common/components/button/SecondaryButton";
import { CustomTable, LinkTd } from "../../common/components/table/CustomTable";
import { T3Text } from "../../common/components/text/CustomText";
import { useNavigate } from "react-router-dom";
import Translate from "../../common/translate/Translate";
import { SimpleTooltip } from "../../common/components/chart/CustomGrpahCommonComponents";
import { Theme } from "../../common/Theme";
import { useWindowSize } from "../../common/hooks/useWindowSize";
import { useDevice } from "../../common/hooks/useDevice";
import RevenueService from "../../service/RevenueService";

const WebGraphGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 17px;
    height: 426px;
`;

const machineGraphKeys = [
    { name: "Combo", color: Theme.color.indigo800, label: "Combo", currency: "฿" },
    { name: "Dryer", color: Theme.color.orange500, label: "Dryer", currency: "฿" },
    { name: "Washer", color: Theme.color.cyan500, label: "Washer", currency: "฿" },
];

export const totalRevenueGraphKeys = [{ name: "revenue", color: Theme.color.indigo500 }];

/**
 * dateRange 값("Today", "7days", "30days", "Custom")을 API용 startDate/endDate(yyyy-MM-dd)로 변환
 */
const getDateParams = (dateRange) => {
    const today = new Date();
    const fmt = (d) => d.toISOString().slice(0, 10);
    const endDate = fmt(today);
    let start = new Date(today);

    if (dateRange === "Today") {
        // 어제 ~ 오늘 (기본값)
        start.setDate(start.getDate() - 1);
    } else if (dateRange === "7days") {
        start.setDate(start.getDate() - 7);
    } else if (dateRange === "30days") {
        start.setDate(start.getDate() - 30);
    } else {
        // Custom 또는 기타: 기본 7일
        start.setDate(start.getDate() - 7);
    }
    return { startDate: fmt(start), endDate };
};

export function RevenueStatusPage() {
    const service = new RevenueService();
    const navigation = useNavigate();
    const { width } = useWindowSize();
    const currentDevice = useDevice();
    const [dateRange, setDateRange] = useState("7days");
    const [data, setData] = useState([]);
    const [machineData, setMachineData] = useState([]);
    const [shopList, setShopList] = useState([]);

    // Total Revenue: 그래프와 같은 일별 매출(04.01.05.01) 합계 — 카드/막대 값 불일치 방지
    const totalRevenue = data.reduce((acc, item) => acc + (Number(item.revenue) || 0), 0);

    const fetchData = (dateRangeValue) => {
        const { startDate, endDate } = getDateParams(dateRangeValue);

        // 일별 매출 그래프 데이터
        service
            .getRevenueDay({ startDate, endDate })
            .then((result) => {
                // spec 04.01.05.01: [{ day, total, washer, dryer, combo }]
                const graphData = (result || []).map((item) => ({
                    name: item.day,
                    revenue: item.total,
                }));
                setData(graphData);
            })
            .catch((error) => {
                console.error("Error fetching revenue day:", error);
            });

        // 매장별 매출 목록 (spec 04.01.05.02: RevenueShopDto[])
        service
            .getRevenueShop({ startDate, endDate })
            .then((result) => {
                const shops = (result || []).map((item, index) => ({
                    number: String(index + 1),
                    shopCode: item.shopCode,
                    shopName: item.shopName,
                    total: item.total,
                    washer: item.washer,
                    dryer: item.dryer,
                    combo: item.combo,
                }));
                setShopList(shops);
            })
            .catch((error) => {
                console.error("Error fetching revenue shop list:", error);
            });

        // 기기별 매출 그래프 데이터 - getRevenueDay로 기기별 분리 데이터가 없으므로
        // getRevenueAnalysis를 활용하여 일별 데이터에 기기별 비율을 적용하거나,
        // 별도 API가 필요할 수 있음. 현재는 getRevenueDay 데이터를 기반으로 표시.
        // TODO: 기기별 일별 매출 API가 제공되면 machineData를 해당 API로 교체
        // Spec 04.01.05.07: [{ shopCode, shopName, total, washer, dryer, combo, ranking }]
        // 합계를 구해 그래프에 표시
        service
            .getRevenueAnalysis({ startDate, endDate })
            .then((result) => {
                const sums = (result || []).reduce(
                    (acc, item) => ({
                        washer: acc.washer + (item.washer || 0),
                        dryer: acc.dryer + (item.dryer || 0),
                        combo: acc.combo + (item.combo || 0),
                    }),
                    { washer: 0, dryer: 0, combo: 0 }
                );
                setMachineData(
                    (data || []).map((item) => ({
                        name: item.name,
                        Washer: sums.washer,
                        Dryer: sums.dryer,
                        Combo: sums.combo,
                    }))
                );
            })
            .catch((error) => {
                console.error("Error fetching revenue analysis:", error);
            });
    };

    useEffect(() => {
        fetchData(dateRange);
    }, [dateRange]);

    const tableHeaders = [
        {
            key: "number",
            label: Translate("common.number"),
        },
        {
            key: "shopCode",
            label: Translate("store.shopCode"),
            render: (row) => (
                <LinkTd
                    href="#"
                    onClick={(e) => {
                        e.preventDefault();
                        navigation(`/shops/${row.shopCode}`);
                    }}
                >
                    {row.shopCode}
                </LinkTd>
            ),
        },
        {
            key: "shopName",
            label: Translate("store.shopName"),
        },
        {
            key: "total",
            label: `${Translate("common.total")}(THB)`,
            render: (row) => <>฿{row.total?.toLocaleString()}</>,
        },
        {
            key: "washer",
            label: `${Translate("common.washer")}(THB)`,
            render: (row) => <>฿{row.washer?.toLocaleString()}</>,
        },
        {
            key: "dryer",
            label: `${Translate("common.dryer")}(THB)`,
            render: (row) => <>฿{row.dryer?.toLocaleString()}</>,
        },
        {
            key: "combo",
            label: `${Translate("common.combo")}(THB)`,
            render: (row) => <>฿{row.combo?.toLocaleString()}</>,
        },
    ];

    const header = (
        <>
            <PageTitle title={Translate("gnb.revenueStatus")} />
            <SizedBox height={"24px"} width={"100%"} />
            <Row width={"100%"} justifyContent={"space-between"}>
                <CustomPeriodButton
                    currentValue={dateRange}
                    setValue={setDateRange}
                    values={[
                        { value: "7days", label: Translate("common.days", ["7"]) },
                        { value: "30days", label: Translate("common.days", ["30"]) },
                    ]}
                    hasCustom={true}
                />
                {currentDevice === "desktop" && (
                    <SecondaryButton icon={"download"} text={Translate("common.exportExcel")} />
                )}
            </Row>
            <SizedBox height={"20px"} width={"100%"} />
        </>
    );

    const totalRevenueGraphCard = (
        <CustomGraphCard
            customData={data}
            title={Translate("dashboard.totalRevenue")}
            mainText={`฿${totalRevenue.toLocaleString()}`}
            isBarChart={true}
            hasButtonSection={false}
            keys={totalRevenueGraphKeys}
            hasLegend={false}
            hasBarLabel={true}
            customTooltip={<SimpleTooltip />}
        />
    );

    const machineRevenueGraphCard = (
        <CustomGraphCard
            customData={machineData}
            title={Translate("revenue.revenueByMachine")}
            hasButtonSection={false}
            isMainTextLegend={true}
            keys={machineGraphKeys}
        />
    );

    const tableArea = (
        <>
            <SizedBox height={"16px"} width={"100%"} />
            <Padding padding={"0 21px"}>
                <T3Text>{Translate("revenue.revenueByShops")}</T3Text>
            </Padding>
            <Divider thickness="0" marginTop="12px" />
            <CustomTable headers={tableHeaders} data={shopList} />
        </>
    );

    if (width <= 1400) {
        return (
            <CommonContentWrapper>
                <Padding padding={"24px 20px"}>
                    {header}
                    {totalRevenueGraphCard}
                    <SizedBox height={"16px"} width={"100%"} />
                    {machineRevenueGraphCard}
                    {tableArea}
                </Padding>
            </CommonContentWrapper>
        );
    }

    return (
        <CommonContentWrapper>
            <Padding padding={"24px 20px"}>
                {header}
                <WebGraphGrid>
                    {totalRevenueGraphCard}
                    {machineRevenueGraphCard}
                </WebGraphGrid>
                {tableArea}
            </Padding>
        </CommonContentWrapper>
    );
}
